import { Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import moment from 'moment';
import { Certificate } from '../../shared/models/certificate.models';
import { CertificateService } from '../../core/services/certificate.service';
import { AsyncPipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../shared/components/table/table.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Status } from '../../shared/models/status-label.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { shareReplay, combineLatest, map, switchMap, filter, take, Subject, startWith } from 'rxjs';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../core/services/status-modal.service';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { SslCertificateInfoDialogComponent } from '../website-overview/dialogs/ssl-certificate-info-dialog/ssl-certificate-info-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { InstallSslCertificateGlobalDialogComponent } from './dialogs/install-ssl-certificate-global-dialog/install-ssl-certificate-global-dialog.component';
import { CertificateType } from '../../shared/models/certificate-type.enum';
import { ImportSslCertificateGlobalDialogComponent } from './dialogs/import-ssl-certificate-global-dialog/import-ssl-certificate-global-dialog.component';

@Component({
  selector: 'app-all-ssl-certificates',
  imports: [
    AsyncPipe,
    FontAwesomeModule,
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    TableComponent,
    SearchFieldComponent,
    StatusLabelComponent,
    MatTooltipModule,
  ],
  templateUrl: './all-ssl-certificates.component.html',
  styleUrl: './all-ssl-certificates.component.scss',
})
export class AllSslCertificatesComponent {
  private readonly certificateService = inject(CertificateService);

  private readonly snackbar = inject(MatSnackBar);

  private readonly statusModalService = inject(StatusModalService);

  private dialog = inject(MatDialog);

  private readonly refresh$ = new Subject<void>();

  readonly pagination = signal({
    pageIndex: 0,
    pageSize: 10,
  });

  readonly searchText = signal('');

  private readonly pagination$ = toObservable(this.pagination);

  private readonly searchText$ = toObservable(this.searchText);

  readonly allCertificates$ = combineLatest({
    domain: this.searchText$,
    refresh: this.refresh$.pipe(startWith(undefined)),
  }).pipe(
    switchMap(({ domain }) => this.certificateService.searchCertificates(1, domain)),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  readonly certificates$ = combineLatest({
    certificates: this.allCertificates$,
    pagination: this.pagination$,
  }).pipe(
    map(({ certificates, pagination: { pageIndex, pageSize } }) => {
      const startIndex = pageIndex * pageSize;

      return certificates.slice(startIndex, startIndex + pageSize);
    }),
  );

  readonly totalCertificates$ = this.allCertificates$.pipe(
    map((certificates) => certificates.length),
  );

  onSearch(text: string): void {
    this.searchText.set(text.trim());

    this.pagination.update((pagination) => ({
      ...pagination,
      pageIndex: 0,
    }));
  }

  handlePageEvent(event: PageEvent): void {
    this.pagination.set({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  readonly certificatesColumns = [
    {
      columnDef: 'domain',
      header: 'Domain',
      cell: (certificate: Certificate) => certificate.domain,
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (certificate: Certificate) => certificate.type,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (certificate: Certificate) => certificate.status ?? '-',
      useTemplate: true,
    },
    {
      columnDef: 'createDate',
      header: 'Creation Date',
      cell: (certificate: Certificate) => this.formatDate(certificate.createDate),
    },
    {
      columnDef: 'expiresAt',
      header: 'Expiration Date',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  formatDate(date?: Date): string {
    return date ? moment(date).format('DD.MM.YYYY, HH:mm:ss') : '-';
  }

  toStatus(key: string): Status {
    return key.toUpperCase() in Status
      ? Status[key.toUpperCase() as keyof typeof Status]
      : Status.ERROR;
  }

  openInstallSSLDialog(): void {
    this.dialog
      .open(InstallSslCertificateGlobalDialogComponent)
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap((data) =>
          data.type === CertificateType.SelfSigned
            ? this.certificateService.createSelfSignedCertificate(data)
            : this.certificateService.createLetsEncryptCertificate(data),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'SSL certificate installation started',
            },
          });

          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error ?? 'SSL certificate installation could not be started.',
          });
        },
      });
  }

  openImportSSLDialog(): void {
    this.dialog
      .open(ImportSslCertificateGlobalDialogComponent)
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap((data) => this.certificateService.registerCustomCertificate(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'SSL certificate installation started',
            },
          });

          this.refresh$.next();
        },
        error: (error) =>
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error ?? 'SSL certificate installation could not be started.',
          }),
      });
  }

  renewCertificate(id: number): void {
    this.certificateService.renewCertificate(id).subscribe({
      next: () =>
        this.snackbar.openFromComponent(SnackbarComponent, {
          data: {
            text: 'Certificate renewal started',
          },
        }),
      error: (error) =>
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error,
        }),
    });
  }

  openCertificateInfoDialog(certificate: Certificate): void {
    this.dialog.open(SslCertificateInfoDialogComponent, {
      data: {
        subTitle: certificate.domain,
        certificate,
      },
    });
  }

  deleteCertificate(certificate: Certificate): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Certificate',
          primaryButtonText: 'Delete SSL Certificate',
          type: 'delete',
          paragraphs: [
            `Are you sure you want to delete the SSL Certificate for ${certificate.domain}?`,
          ],
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => this.certificateService.deleteCertificate(certificate.id)),
      )
      .subscribe({
        next: () => {
          this.pagination.update((pagination) => ({
            ...pagination,
            pageIndex: 0,
          }));

          this.refresh$.next();

          this.statusModalService.open({
            state: 'success',
            message: 'SSL Certificate deleted successfully',
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }
}
